import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "@remix-run/react";
import { AlertTriangle, CircleCheckBig, X } from "lucide-react";

import {
  Drawer,
  DrawerClose,
  DrawerContent,
} from "~/components/ui/custom-bottom-drawer";
import { Input } from "~/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";

import {
  LEAD_FORMS,
  LEAD_STATUS,
  trackAddListingRequestConversion,
  trackLead,
  trackFormOpen,
} from "~/lib/ConversionTracking";
import EmailService from "~/lib/services/EmailService";
import HomeServices from "~/lib/services/HomeServices";
import ListingsService from "~/lib/services/ListingsService";
import SalesForceServices from "~/lib/services/SalesForceServices";
import { useCurrentRegion } from "~/lib/helpers/CurrencyHelper";
import { getCurrentCustomerCount } from "~/lib/helpers/StringHelper";
import {
  getSalesforceAdClickIds,
  getLandingPageUrl,
} from "~/lib/helpers/adClickIds";
import { getCountryData, getRegionEmail } from "custom-config/region.configs";
import { isCommonFreeEmail } from "~/lib/helpers/commonFreeEmail";

import PhoneInput from "react-phone-input-2";
import { useForm } from "react-hook-form";

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  accountName: string;
  mobilePhone?: string;
};

type SubmitMessage =
  | null
  | {
    icon: React.ReactNode;
    title: string;
    description: string;
    type: "success" | "error";
  };

function parseEmailVerifyResult(data: unknown) {
  const payload = (data as any)?.data ?? data;
  const isFreemailRaw =
    (payload as any)?.["is-freemail"] ??
    (payload as any)?.isFreemail ??
    (payload as any)?.["is_freemail"];
  const isFreemail =
    typeof isFreemailRaw === "string"
      ? isFreemailRaw.toLowerCase() === "true"
      : Boolean(isFreemailRaw);
  const isVerified =
    Boolean((payload as any)?.verified) || Boolean((payload as any)?.valid);
  return { isFreemail, isVerified };
}

export default function AddListingTwoStepDrawer({
  onClose,
}: {
  onClose: () => void;
}) {
  // Every "Add Listing" CTA is a button that opens this, never a link, so the
  // delegated link listener never sees it. This component only renders when
  // it is shown, so mounting is the open.
  useEffect(() => {
    trackFormOpen(LEAD_FORMS.ADD_LISTING);
  }, []);

  const [loading, setLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<SubmitMessage>(null);
  const [submitUrl, setSubmitUrl] = useState<string | null>(null);
  const [phoneCountry, setPhoneCountry] = useState<string>("");
  const [regionRating, setRegionRating] = useState<string>("5.0");
  const [emailVerifying, setEmailVerifying] = useState(false);
  const emailVerifyRequestId = useRef(0);
  const submitInFlightRef = useRef(false);
  const partialCloseSubmitSentRef = useRef(false);

  const regionConfig = useCurrentRegion();
  const regionCode = regionConfig?.region || "us";
  const countryData = useMemo(() => getCountryData(regionCode), [regionCode]);
  const supportPhoneHref = countryData?.phoneNumberWithCode || "";
  const supportPhoneLabel =
    countryData?.phoneNumberWithCode || countryData?.phoneNumber || "";
  const supportEmail = useMemo(() => getRegionEmail(regionCode), [regionCode]);

  const phoneDefaultCountry = useMemo(() => {
    const region = regionConfig?.region;
    if (!region) return undefined;
    return region === "uk" ? "gb" : region;
  }, [regionConfig?.region]);

  useEffect(() => {
    const region = regionConfig?.region;
    if (!region) return;

    let cancelled = false;
    HomeServices.getHomeSummary(region)
      .then((data: any) => {
        const raw = data?.sumRatings;
        const num = Number.parseFloat(String(raw ?? ""));
        const rating = Number.isFinite(num) && num > 0 ? num.toFixed(2) : "5.0";
        if (!cancelled) setRegionRating(rating);
      })
      .catch(() => {
        if (!cancelled) setRegionRating("5.0");
      });

    return () => {
      cancelled = true;
    };
  }, [regionConfig?.region]);

  const [searchParams] = useSearchParams();
  const device = searchParams.get("device") || "";
  const keywords = searchParams.get("keyword") || "";
  const campaign = searchParams.get("campaign") || "";

  const form = useForm<FormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      accountName: "",
      mobilePhone: "",
    },
    mode: "onSubmit",
    reValidateMode: "onBlur",
  });

  async function verifyBusinessEmail(value: string): Promise<boolean> {
    form.clearErrors("email");
    const trimmed = value.trim();
    if (!trimmed) {
      form.setError("email", {
        type: "required",
        message: "Enter your Email",
      });
      return false;
    }

    const requestId = ++emailVerifyRequestId.current;
    if (isCommonFreeEmail(trimmed)) {
      form.setError("email", {
        type: "validate",
        message: "Please enter a valid business email address",
      });
      return false;
    }

    setEmailVerifying(true);
    try {
      const data = await EmailService.verifyEmail(encodeURIComponent(trimmed));
      if (requestId !== emailVerifyRequestId.current) return false;

      const { isFreemail, isVerified } = parseEmailVerifyResult(data);
      if (isFreemail || !isVerified) {
        form.setError("email", {
          type: "validate",
          message: "Please enter a valid business email address",
        });
        return false;
      }
      return true;
    } catch {
      if (requestId !== emailVerifyRequestId.current) return false;
      form.setError("email", {
        type: "validate",
        message: "Could not validate email. Try again.",
      });
      return false;
    } finally {
      if (requestId === emailVerifyRequestId.current) setEmailVerifying(false);
    }
  }

  async function handleSubmit(values: FormValues) {
    setSubmitMessage(null);
    setSubmitUrl(null);

    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      accountName: values.accountName,
      mobilePhone: `+${values.mobilePhone}`,
      country: phoneCountry || regionConfig?.country,
      formUrl: typeof window !== "undefined" ? window.location.href : "",
      ...getSalesforceAdClickIds(),
      device,
      ppcKeywords: keywords,
      campaign,
      userLandingUrl: getLandingPageUrl(),
    };

    try {
      const response = await SalesForceServices.addListing({ contact: payload });

      if (response?.errorCode === "200") {
        trackAddListingRequestConversion({
          email: payload.email,
          phone_number: payload.mobilePhone,
        });
        trackLead({
          form: LEAD_FORMS.ADD_LISTING,
          status: LEAD_STATUS.COMPLETE,
          userData: {
            email: payload.email,
            phone_number: payload.mobilePhone,
          },
        });

        ListingsService.sendSuccessNotification(
          { ...payload, sfApiResponse: response },
          "add-listing",
        );

        setSubmitMessage({
          icon: (
            <CircleCheckBig className="h-6 w-6 text-gray-700" strokeWidth={2} />
          ),
          title: "Let’s create your workspace and listings!",
          description:
            "MyOfficeHub is your dedicated dashboard exclusive to Office Hub where you can manage, create and edit your properties, listings and leads. From sales insights to adding a listing, everything you need to manage your workspace is now one click away.",
          type: "success",
        });
      } else {
        ListingsService.sendErrorNotification(
          { ...payload, sfApiResponse: response },
          "add-listing",
        );

        setSubmitMessage({
          icon: <AlertTriangle className="h-5 w-5 text-gray-700" />,
          title: "This email is already registered.",
          description:
            "This email is already associated with an account. Sign in or reset your password to continue.",
          type: "error",
        });
      }

      setSubmitUrl(response?.myOHUrl ?? null);
    } catch (err: any) {
      setSubmitMessage({
        icon: <AlertTriangle className="h-5 w-5 text-gray-700" />,
        title: "Something went wrong.",
        description: err?.message ? `Error: ${err.message}` : "Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  async function onSubmitButtonClick() {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setLoading(true);
    try {
      const nonEmailOk = await form.trigger(
        ["firstName", "lastName", "mobilePhone", "accountName"],
        { shouldFocus: true },
      );
      if (!nonEmailOk) return;
      const values = form.getValues();
      form.clearErrors("email");
      const emailAccepted = await verifyBusinessEmail(values.email ?? "");
      if (!emailAccepted) return;
      await handleSubmit(values);
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
    }
  }

  async function submitPartialOnCloseIfNeeded() {
    if (partialCloseSubmitSentRef.current) return;
    if (loading) return;
    const v = form.getValues();
    const email = (v.email ?? "").trim();
    if (!email) return;
    if (submitMessage?.type === "success") return;

    partialCloseSubmitSentRef.current = true;
    try {
      const userLandingUrl = getLandingPageUrl();
      const payload = {
        firstName: (v.firstName ?? "").trim(),
        lastName: (v.lastName ?? "").trim(),
        email,
        accountName: (v.accountName ?? "").trim(),
        mobilePhone: v.mobilePhone ? `+${v.mobilePhone}` : "",
        country: phoneCountry || regionConfig?.country,
        formUrl: typeof window !== "undefined" ? window.location.href : "",
        ...getSalesforceAdClickIds(),
        device,
        ppcKeywords: keywords,
        campaign,
        userLandingUrl,
      };
      const response = await SalesForceServices.addListing({ contact: payload });
      if (response?.errorCode === "200") {
        trackLead({
          form: LEAD_FORMS.ADD_LISTING,
          status: LEAD_STATUS.PARTIAL,
          userData: {
            email: payload.email,
            phone_number: payload.mobilePhone,
          },
        });
      }
    } catch {
      partialCloseSubmitSentRef.current = false;
    }
  }

  function handleDrawerOpenChange(open: boolean) {
    if (!open) {
      void submitPartialOnCloseIfNeeded();
      onClose();
    }
  }

  return (
    <Drawer open onOpenChange={handleDrawerOpenChange}>
      <DrawerContent className="max-w-xl w-full max-h-[95dvh] flex flex-col mx-auto h-[95dvh] overflow-hidden">
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b shrink-0 bg-white">
          <div />

          <DrawerClose asChild>
            <button
              type="button"
              className="text-gray-500 hover:text-black touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center -m-2"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </DrawerClose>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 bg-white">
          {submitMessage ? (
            <div className="bg-white rounded-lg p-0">
              <div className="flex items-center space-x-2">
                {submitMessage.icon}
                <h2 className="text-gray-900 font-semibold text-lg">
                  {submitMessage.title}
                </h2>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                {submitMessage.description}
              </p>

              <div className="mt-6 flex justify-end space-x-4">
                {submitMessage.type === "error" && submitUrl && (
                  <Link
                    to={`${submitUrl}?resetpassword=true`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-gray-800 hover:underline"
                  >
                    Reset password
                  </Link>
                )}

                {submitUrl && (
                  <Link
                    to={submitUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-800"
                  >
                    {submitMessage.type === "success" ? "Get Started" : "Login"}
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-[34px] font-extrabold leading-[1.05] text-oh-primary">
                Rent Out Your <br />
                Spare Office Space
              </h2>
              <p className="mt-3 text-[14px] text-gray-800 max-w-[360px]">
                Join the world’s biggest marketplace for flexible and shared
                office space.
              </p>

              <Form {...form}>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void onSubmitButtonClick();
                  }}
                  className="mt-6 space-y-4"
                >
                  <div className="grid grid-cols-1 gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="firstName"
                        rules={{ required: "Enter your First Name" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-900">
                              First Name
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="First Name"
                                {...field}
                                className="h-9 text-sm rounded-md"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="lastName"
                        rules={{ required: "Enter your Last Name" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-900">
                              Last Name
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Last Name"
                                {...field}
                                className="h-9 text-sm rounded-md"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-900">
                            * Email Address
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="email@yourcompany.com"
                              name={field.name}
                              ref={field.ref}
                              value={field.value}
                              onChange={(e) => {
                                field.onChange(e);
                                emailVerifyRequestId.current += 1;
                                setEmailVerifying(false);
                                form.clearErrors("email");
                              }}
                              onBlur={() => {
                                field.onBlur();
                                if (submitInFlightRef.current) return;
                                void verifyBusinessEmail(field.value);
                              }}
                              className="h-9 text-sm rounded-md"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="mobilePhone"
                      rules={{ required: "Enter your Mobile Number" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-900 text-xs">
                            Mobile Number
                          </FormLabel>
                          <FormControl>
                            <PhoneInput
                              country={phoneDefaultCountry as any}
                              value={field.value}
                              onChange={(value, data) => {
                                field.onChange(value);
                                setPhoneCountry((data as any)?.name || "");
                              }}
                              inputStyle={{
                                width: "100%",
                                paddingLeft: 60,
                                borderColor: "#e6e6e6",
                                height: "36px",
                              }}
                              buttonStyle={{
                                width: 50,
                                borderColor: "#e6e6e6",
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="accountName"
                      rules={{ required: "Enter your Company" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-900">
                            Company
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Please enter your company name"
                              {...field}
                              className="h-9 text-sm rounded-md"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-2 w-full h-11 bg-gray-900 text-white rounded-md text-sm font-semibold hover:bg-gray-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </button>

                  <p className="mt-3 text-xs text-gray-700 text-center leading-5">
                    I consent to having &quot;Office Hub Pty Limited&quot; collect my name,
                    email and phone number.
                  </p>
                </form>
              </Form>



              {/* <CustomerRatingRow regionRating={regionRating} /> */}
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
